/* eslint-disable eslint-compat/camelcase */
import { daysInYear, millisecondsInDay } from 'date-fns/constants';
import jwtDecode from 'jwt-decode';

import { ACCESS_TOKEN_COOKIE_KEY, REFRESH_TOKEN_COOKIE_KEY } from '@pflegia/types/common/constants/tokens';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { getTopLevelDomain } from '@pflegia/util/common/get-top-level-domain';
import { isDefined } from '@pflegia/util/functions/type-guards';

import { ANALYTICS_NOT_INITIALIZED } from '../../analytics/constants/errors';
import { ABTestingService } from '../../interfaces/ab-testing-service.interface';
import { AnalyticsService } from '../../interfaces/analytics-service.interface';
import { IdentityService } from '../../interfaces/identity-service.interface';
import { MethodExecutionGuard } from '../../utils/decorators/method-execution-guard.decorator';

type DecodedToken = { email: string; sub: string };

export class UnwrappedBaseAccessTokenService implements IdentityService {
  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly abTestingService?: ABTestingService,
  ) {}
  get() {
    return cookieStorage.getItem(ACCESS_TOKEN_COOKIE_KEY);
  }
  set(value: string) {
    const domain = getTopLevelDomain(globalThis.window.location.hostname);
    cookieStorage.setItem(ACCESS_TOKEN_COOKIE_KEY, value, {
      sameSite: 'Lax',
      secure: process.env.NEXT_PUBLIC_ENV !== 'development',
      expires: new Date(Date.now() + daysInYear * millisecondsInDay),
      domain,
    });
    this.identifyUser();
  }
  remove() {
    const domain = getTopLevelDomain(globalThis.window.location.hostname);
    cookieStorage.removeItem(ACCESS_TOKEN_COOKIE_KEY, { domain });
    cookieStorage.removeItem(REFRESH_TOKEN_COOKIE_KEY, { domain });
    this.abTestingService?.reset();
  }
  getDecodedToken<T = DecodedToken>() {
    const token = this.get();
    if (token) {
      return jwtDecode<T>(token);
    }
    return null;
  }

  identifyUser() {
    try {
      if (!('getID' in this.analyticsService)) throw new Error(ANALYTICS_NOT_INITIALIZED);

      const decodedToken = this.getDecodedToken();
      const currentAbTestingId = this.abTestingService?.getID();
      const userId = this.analyticsService.getID();

      if (isDefined(decodedToken)) {
        if (userId !== decodedToken.sub) this.analyticsService.identify(decodedToken.sub);
        if (currentAbTestingId !== decodedToken.sub) this.abTestingService?.identify(decodedToken.sub, { internal_id: decodedToken.sub });
      } else if (userId && currentAbTestingId !== userId) {
        this.abTestingService?.identify(userId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}

export const BaseAccessTokenService = MethodExecutionGuard({
  checkCondition: () => typeof window !== 'undefined',
})(UnwrappedBaseAccessTokenService);
